import React, { useMemo, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import OrgFormFields from './OrgFormFields';
import OrgPickerList from './OrgPickerList';
import { setOrgActive, updateOrg } from './api';
import { AdminOrg, formFromOrg, OrgFormValue, OrgsMeta } from './types';
import { dangerBtnClass, primaryBtnClass, secondaryBtnClass } from '../styles';

interface EditOrganizationProps {
  meta: OrgsMeta;
}

const EditOrganization: React.FC<EditOrganizationProps> = ({ meta }) => {
  const [selected, setSelected] = useState<AdminOrg | null>(null);
  const [value, setValue] = useState<OrgFormValue | null>(null);
  const [listKey, setListKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onPick = (_ids: number[], orgs: AdminOrg[]) => {
    const org = orgs[0] || null;
    setSelected(org);
    setValue(org ? formFromOrg(org) : null);
    setError(null);
    setSuccess(null);
  };

  const dirty = useMemo(() => {
    if (!selected || !value) return false;
    return JSON.stringify(formFromOrg(selected)) !== JSON.stringify(value);
  }, [selected, value]);

  const save = async () => {
    if (!selected || !value) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateOrg(selected.id, value);
      setSelected(updated);
      setValue(formFromOrg(updated));
      setSuccess(`Saved ${updated.name}.`);
      setListKey((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save organization');
    } finally {
      setSaving(false);
    }
  };

  const backToList = () => {
    if (dirty && !window.confirm('You have unsaved changes. Go back to the list anyway?')) {
      return;
    }
    setSelected(null);
    setValue(null);
    setError(null);
    setSuccess(null);
    setListKey((n) => n + 1);
  };

  const toggleActive = async () => {
    if (!selected) return;
    const next = !selected.is_active;
    const verb = next ? 'reactivate' : 'deactivate';
    if (!window.confirm(`${verb[0].toUpperCase()}${verb.slice(1)} ${selected.name}?`)) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await setOrgActive(selected.id, next);
      setSelected(updated);
      setValue(formFromOrg(updated));
      setSuccess(next ? `Reactivated ${updated.name}.` : `Deactivated ${updated.name}.`);
      setListKey((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${verb} organization`);
    } finally {
      setSaving(false);
    }
  };

  if (selected && value) {
    return (
      <div className="max-w-3xl">
        <button
          type="button"
          onClick={backToList}
          disabled={saving}
          className="inline-flex items-center gap-1 text-sm text-[#3da3d4] hover:text-[#2d8ab8] mb-4 disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to list
        </button>
        <h3 className="text-base font-semibold text-[#0C0B0B] mb-4">Edit {selected.name}</h3>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
            {success}
          </div>
        )}
        <OrgFormFields
          meta={meta}
          value={value}
          onChange={setValue}
          mode="edit"
          disabled={saving}
        />
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving || !dirty}
            onClick={() => void save()}
            className={primaryBtnClass}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
            Save changes
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void toggleActive()}
            className={selected.is_active ? dangerBtnClass : secondaryBtnClass}
          >
            {selected.is_active ? 'Deactivate organization' : 'Reactivate organization'}
          </button>
          <button type="button" disabled={saving} onClick={backToList} className={secondaryBtnClass}>
            Back to list
          </button>
        </div>
      </div>
    );
  }

  return (
    <OrgPickerList
      meta={meta}
      mode="single"
      selectedIds={[]}
      onChange={onPick}
      refreshToken={listKey}
    />
  );
};

export default EditOrganization;
